import { c as _c } from "react/compiler-runtime";
import { SkeletonBar, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type InputHTMLAttributes, type ReactNode, useId } from 'react';
import type { AutoshipDiscountMessages } from '../lib';
import './autoship-purchase-options.scss';
export type PurchaseOption = 'one-time' | 'autoship';
type PurchaseOptionInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id' | 'value' | 'className' | 'disabled'>;
type AutoshipPurchaseOptionsProps = Readonly<{
  selected: PurchaseOption;
  inputProps: PurchaseOptionInputProps;
  isAutoshipDisabled: boolean;
  isLoading: boolean;
  messages: AutoshipDiscountMessages;
  footnote?: string;
  oneTimePriceSlot?: ReactNode;
  infoSlot?: ReactNode;
  children?: ReactNode;
  testId?: string;
}>;
type PurchaseOptionCardProps = Readonly<{
  id: string;
  value: PurchaseOption;
  isChecked: boolean;
  inputProps: PurchaseOptionInputProps;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}>;
const PurchaseOptionCard = t0 => {
  const $ = _c(13);
  const {
    id,
    value,
    isChecked,
    inputProps,
    disabled,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== isChecked) {
    t1 = cn("purchase-options__option", className, {
      "purchase-options__option--checked": isChecked
    });
    $[0] = className;
    $[1] = isChecked;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== disabled || $[4] !== id || $[5] !== inputProps || $[6] !== value) {
    t2 = <input className="radio" type="radio" value={value} id={id} {...inputProps} disabled={disabled} />;
    $[3] = disabled;
    $[4] = id;
    $[5] = inputProps;
    $[6] = value;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== id || $[10] !== t1 || $[11] !== t2) {
    t3 = <label className={t1} htmlFor={id}>{children}{t2}</label>;
    $[8] = children;
    $[9] = id;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
export const AutoshipPurchaseOptions = t0 => {
  const $ = _c(36);
  const {
    selected,
    inputProps,
    isAutoshipDisabled,
    isLoading,
    messages,
    footnote,
    oneTimePriceSlot,
    infoSlot,
    children,
    testId: t1
  } = t0;
  const testId = t1 === undefined ? "pdp-purchase-options" : t1;
  const idPrefix = useId();
  const isAutoshipSelected = selected === "autoship";
  const t2 = `${idPrefix}-one-time`;
  const t3 = selected === "one-time";
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TextPassage className="purchase-options__option--title" size="lg">One-time purchase</TextPassage>;
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== oneTimePriceSlot) {
    t5 = <div className="purchase-options__option--title-container">{t4}{oneTimePriceSlot}</div>;
    $[1] = oneTimePriceSlot;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== inputProps || $[4] !== t2 || $[5] !== t3 || $[6] !== t5) {
    t6 = <PurchaseOptionCard id={t2} value="one-time" isChecked={t3} inputProps={inputProps}>{t5}</PurchaseOptionCard>;
    $[3] = inputProps;
    $[4] = t2;
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = `${idPrefix}-autoship`;
  let t8;
  if ($[8] !== isLoading || $[9] !== messages) {
    t8 = isLoading ? <SkeletonBar className="purchase-options__option--title-skeleton" /> : messages.autoshipDiscountHeader;
    $[8] = isLoading;
    $[9] = messages;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <TextPassage className="purchase-options__option--title" size="lg">{t8}</TextPassage>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== infoSlot || $[14] !== t9) {
    t10 = <div className="purchase-options__option--title-container">{t9}{infoSlot}</div>;
    $[13] = infoSlot;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== footnote || $[17] !== isLoading || $[18] !== messages) {
    t11 = <div className="purchase-options__option--content-text">{isLoading ? <SkeletonBar className="purchase-options__option--content-text-skeleton" /> : <><TextPassage>{messages.autoshipDiscountSubtext}</TextPassage>{messages.autoshipDiscountSubtextTwo && <TextPassage>{messages.autoshipDiscountSubtextTwo}</TextPassage>}{footnote && <TextPassage size="sm">{footnote}</TextPassage>}</>}</div>;
    $[16] = footnote;
    $[17] = isLoading;
    $[18] = messages;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = isAutoshipSelected && children;
  let t13;
  if ($[20] !== t11 || $[21] !== t12) {
    t13 = <div className="purchase-options__option--content">{t11}{t12}</div>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t10 || $[24] !== t13) {
    t14 = <div className="purchase-options__option--autoship-main"><div className="purchase-options__option--body">{t10}{t13}</div></div>;
    $[23] = t10;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== inputProps || $[27] !== isAutoshipDisabled || $[28] !== isAutoshipSelected || $[29] !== t14 || $[30] !== t7) {
    t15 = <PurchaseOptionCard id={t7} value="autoship" isChecked={isAutoshipSelected} inputProps={inputProps} disabled={isAutoshipDisabled} className="purchase-options__option--autoship">{t14}</PurchaseOptionCard>;
    $[26] = inputProps;
    $[27] = isAutoshipDisabled;
    $[28] = isAutoshipSelected;
    $[29] = t14;
    $[30] = t7;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t6 || $[34] !== testId) {
    t16 = <fieldset className="purchase-options" data-testid={testId}>{t6}{t15}</fieldset>;
    $[32] = t15;
    $[33] = t6;
    $[34] = testId;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
};